import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import SelectedApplicants from '../SelectedApplicants';
import { IExtendedActionModalProps } from '../../types';
import { classes } from '../styles';
import { useQuery } from '@tanstack/react-query';
import Api from '../../../Application/API';
import { Autocomplete, TextField } from '@mui/material';

export default function BulkExportCSV({
  jobId,
  setSnackbar,
  modalsOpen,
  setModalsOpen,
  globalSelected,
  handleDeselectCandidate,
  filters
}: IExtendedActionModalProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<{ value: number; label: string } | null>(
    null
  );
  const [candidateData, setCandidateData] = useState<string[]>([]);
  const [templateRequiredError, setTemplateRequiredError] = useState<string>('');
  const { data: templates, isLoading: loadingTemplates } = useQuery({
    queryKey: ['csv export templates'],
    queryFn: async () => {
      const { res } = await Api.getCsvExportTemplates();
      const templates = res as { csv_export_templates: Array<{ value: number; label: string }> };
      return templates.csv_export_templates;
    },
    onError: (e: { error: string }) =>
      setSnackbar({
        message: `There was an error getting export templates: ${e.error}`,
        state: 'error'
      }),
    initialData: [],
    enabled: globalSelected.length > 0
  });

  const {
    isFetching: loadingCandidateData,
    refetch: fetchCandidateData,
    isSuccess: candidateDataSuccess
  } = useQuery({
    queryKey: ['csv export candidate data', globalSelected.map((app) => app.id)],
    queryFn: async () => {
      if (globalSelected.length === 0 || !selectedTemplate) return;
      const { res } = await Api.getCandidateDataWithCsvTemplate(
        jobId,
        globalSelected.map((app) => app.id),
        selectedTemplate.value
      );
      const result = res as {
        csv: { rows: string[][] };
        meta: Array<{ label: string; column: string }>;
      };

      const headers = result.meta.map((row: { column: string }) => `"${row.column}"`).join(',');
      const rows = result.csv.rows.map((row: string[]) =>
        row.map((cell: string | null) => (cell ? `"${cell}"` : '')).join(',')
      );

      setCandidateData([headers, ...rows]);
      return result;
    },
    onError: (e: { error: string }) =>
      setSnackbar({
        message: `There was an error getting candidate data: ${e.error}`,
        state: 'error'
      }),
    initialData: [],
    enabled: false
  });

  useEffect(() => {
    if (candidateDataSuccess && candidateData.length > 0) {
      const csvString = candidateData.join('\n');

      const blob = new Blob([csvString], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'candidate_export.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setModalsOpen((prev) => ({ ...prev, exportToCsv: false }));

      setSnackbar({
        message: 'Your CSV file has been downloaded',
        state: 'success'
      });
    }
  }, [candidateData, candidateDataSuccess, setModalsOpen, setSnackbar]);

  return (
    <Modal
      open={modalsOpen.exportToCsv}
      onClose={() => setModalsOpen((prev) => ({ ...prev, exportToCsv: false }))}
      aria-labelledby="export-csv-modal"
    >
      <Box sx={classes.actionsModalReducedPadding}>
        <CloseIcon
          onClick={() => setModalsOpen((prev) => ({ ...prev, exportToCsv: false }))}
          sx={classes.closeIcon}
        />
        <Box id="export-csv-title" sx={classes.actionModalHeader}>
          Export to CSV
        </Box>
        <Box>
        <Autocomplete
            disablePortal
            options={templates}
            getOptionLabel={(option: { label: string }) => option.label}
            value={selectedTemplate}
            sx={{ paddingTop: 4, flexGrow: 1 }}
            loading={loadingTemplates}
            loadingText="Loading templates..."
            renderInput={(params: object) => (
              <TextField
                {...params}
                label="Which service would you like to export to?"
                InputLabelProps={{ shrink: true }}
                required
                helperText={templateRequiredError}
              />
            )}
            onChange={(_event, value) => {
              value
                ? setTemplateRequiredError('')
                : setTemplateRequiredError('Must select a template');
              setSelectedTemplate(value);
            }}
          />
        </Box>
        <Box
          sx={classes.modalContent}
          id="export-csv-modal-content"
          aria-label="Export CSV Modal Content"
        >
          <Box sx={{ marginLeft: '15px' }}>
            <h3>Candidates</h3>
            <SelectedApplicants
              globalSelected={globalSelected}
              handleDeselectCandidate={handleDeselectCandidate}
              closeModalCallback={() => setModalsOpen((prev) => ({ ...prev, exportToCsv: false }))}
              filters={filters}
            />
          </Box>
        </Box>
        <Box sx={classes.modalFooter}>
          <Button
            id="close-export-csv-button"
            sx={classes.modalCancelButton}
            onClick={() => setModalsOpen((prev) => ({ ...prev, exportToCsv: false }))}
          >
            Cancel
          </Button>
          <Button
            id="submit-export-csv-button"
            type="submit"
            sx={{ ...classes.modalSendButton, width: '128px', height: '41px' }}
            onClick={(e) => {
              e.preventDefault();
              fetchCandidateData();
            }}
          >
            {loadingTemplates || loadingCandidateData ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Export to CSV'
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
